import React, { useState } from "react";

/**
 * `B.12` (п. 18): перевыпуск и отзыв ключа API в карточке организации.
 *
 * Ключ показывается **один раз** — сразу после перевыпуска. Закрыли окно или
 * переоткрыли карточку — ключа больше нет: сервер его не отдаёт, хранится только
 * хэш. Обе операции требуют подтверждения; кнопок нет у не-root.
 */

type ConfirmAction = "reissue" | "revoke" | null;

function messageOf(error: unknown): string {
  const value = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };

  return (
    value?.response?.data?.error ||
    value?.response?.data?.message ||
    value?.message ||
    "Не удалось выполнить действие"
  );
}

export default function OrgApiKeyActions({
  isRoot,
  onReissue,
  onRevoke,
}: {
  isRoot: boolean;
  onReissue: () => Promise<string>;
  onRevoke: () => Promise<void>;
}) {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!isRoot) return null;

  const closeKey = () => {
    setIssuedKey(null);
    setCopied(false);
  };

  const run = async () => {
    if (!confirmAction) return;

    setBusy(true);
    setError(null);

    try {
      if (confirmAction === "reissue") {
        const key = await onReissue();
        setIssuedKey(key);
        setCopied(false);
      } else {
        await onRevoke();
      }

      setConfirmAction(null);
    } catch (requestError) {
      setError(messageOf(requestError));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!issuedKey) return;

    try {
      await navigator.clipboard.writeText(issuedKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="org-api-key-actions">
      {!confirmAction && !issuedKey ? (
        <div className="org-api-key-actions__buttons">
          <button
            type="button"
            className="org-api-key-actions__button"
            onClick={() => setConfirmAction("reissue")}
            disabled={busy}
          >
            Перевыпустить ключ
          </button>
          <button
            type="button"
            className="org-api-key-actions__button org-api-key-actions__button--danger"
            onClick={() => setConfirmAction("revoke")}
            disabled={busy}
          >
            Отозвать ключ
          </button>
        </div>
      ) : null}

      {confirmAction ? (
        <div
          className="org-api-key-confirm"
          role="alertdialog"
          aria-label="Подтверждение действия с ключом"
        >
          <p>
            {confirmAction === "reissue"
              ? "Перевыпустить ключ? Старый ключ сразу перестанет работать."
              : "Отозвать ключ? Организация перестанет находиться по ключу."}
          </p>
          <div className="org-api-key-actions__buttons">
            <button
              type="button"
              className="org-api-key-actions__button org-api-key-actions__button--danger"
              onClick={run}
              disabled={busy}
            >
              {confirmAction === "reissue"
                ? "Да, перевыпустить"
                : "Да, отозвать"}
            </button>
            <button
              type="button"
              className="org-api-key-actions__button"
              onClick={() => setConfirmAction(null)}
              disabled={busy}
            >
              Отмена
            </button>
          </div>
        </div>
      ) : null}

      {issuedKey ? (
        <div
          className="org-api-key-once"
          role="dialog"
          aria-label="Новый ключ API"
        >
          <p className="org-api-key-once__warning">
            Ключ показывается один раз. Скопируйте его сейчас — после закрытия
            окна его не будет.
          </p>
          <input
            className="org-api-key-once__value"
            aria-label="Новый ключ API"
            value={issuedKey}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
          />
          <div className="org-api-key-actions__buttons">
            <button
              type="button"
              className="org-api-key-actions__button"
              onClick={copy}
            >
              Копировать
            </button>
            <button
              type="button"
              className="org-api-key-actions__button"
              onClick={closeKey}
            >
              Закрыть
            </button>
          </div>
          {copied ? (
            <span className="org-api-key-once__copied" role="status">
              Скопировано
            </span>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="org-api-key-actions__error" role="status">
          {error}
        </p>
      ) : null}
    </div>
  );
}
